<template>
  <div v-if="user">
    <b-container class="p-relative">
      <div class="header-text-container">
        <div
          class="header-text"
          :class="isMobile ? 'mt-2' : ''"
          v-if="showHeader"
        >
          Hi {{ user.full_name ? user.full_name.split(' ')[0] : '' }}
        </div>
        <div class="subheader-text" v-if="!isMobile">
          Welcome back to your account.
        </div>
      </div>
      <div class="header-actions-container">
        <search-all
          @open="showHeader = isMobile ? false : true"
          @close="showHeader = true"
        />

        <b-button
          class="py-2 px-3 ml-3"
          @click="inviteUserModal = true"
          v-if="isOperation"
        >
          <b-icon scale="1.3" icon="plus"></b-icon>
          <b class="ml-2" v-if="!isMobile">Invite New User</b>
        </b-button>
        <b-button class="py-2 px-3 ml-3" @click="show = true">
          <b-img
            width="24"
            class="rounded-0"
            :src="require('@/assets/icons/qr-code.svg')"
          ></b-img>
          <b class="ml-2" v-if="!isMobile">Scan QR Code</b>
        </b-button>
      </div>
    </b-container>

    <div class="cover-image">
      <div class="cover-overlay"></div>
    </div>

    <b-modal v-model="show" title="Scan QR Code" hide-footer centered size="md">
      <qrcode-stream @decode="onDecode"></qrcode-stream>
      <div class="mt-4">{{ errorMessage }}</div>
    </b-modal>

    <b-container class="py-0 mt-5" :class="isMobile ? '' : 'custom-container'">
      <site-list @openScanner="show = true" />
    </b-container>

    <b-container class="py-5" :class="isMobile ? '' : 'custom-container'">
      <board-list @openScanner="show = true" />
    </b-container>
    <b-modal
      v-model="inviteUserModal"
      title="Invite User"
      title-class="text-center w-100 "
      hide-footer
      centered
      size="md"
      @hidden="cancel"
    >
      <h6 class="text-center">Enter an Email address to send an invitation:</h6>
      <div class="email-list emails  mb-2">
        <div class="d-flex flex-column" v-if="form.emails.length">
          <span>Selected Emails</span>
        </div>
        <b-badge
          pill
          v-for="(email, index) in form.emails"
          :key="`${email}-${index}`"
          class="p-2 font-weight-normal"
        >
          {{ email }}
          <b-icon
            icon="x-circle-fill"
            class="ml-2 cursor-pointer"
            @click="form.emails.splice(index, 1)"
          ></b-icon>
          <!-- <i class="icon icon-remove" @click="form.emails.splice(index, 1)"></i> -->
        </b-badge>
      </div>

      <b-form>
        <b-form-group id="input-group-full-name" class="col-12">
          <i class="clearable" v-show="form.email" @click="form.email = ''"
            >&times;</i
          >
          <b-form-input
            v-b-tooltip.hover.bottom
            title="Press enter to add email"
            ref="refFullName"
            v-model="form.email"
            @keyup.enter="handleEmail"
            @blur="handleEmail"
            class="input-effect"
            :class="form.email ? 'has-content' : ''"
            type="text"
            required
            aria-describedby="input-full-name-feedback"
          ></b-form-input>
          <label>Email</label>

          <div class="input-error" v-if="emailErrors.length || siteErrors[0]">
            {{ emailErrors[0] || siteErrors[0] }}
          </div>
        </b-form-group>

        <b-form-group class="col-12">
          <b-img
            class="append-icon"
            :src="require('@/assets/icons/chevron-secondary.svg')"
            @click="showDropdown.accountType = !showDropdown.accountType"
          ></b-img>
          <b-form-input
            v-model="form.accountType.text"
            class="input-effect"
            :class="form.accountType.value ? 'has-content' : ''"
            :readonly="true"
            type="text"
            required
            @focus="showDropdown.accountType = true"
            @blur="showDropdown.accountType = false"
          ></b-form-input>
          <label>Account Type</label>

          <ul v-show="showDropdown.accountType" @mousedown.prevent>
            <li
              v-for="(item, index) in accountTypes"
              :key="index"
              @click="
                form.accountType = item
                showDropdown.accountType = false
              "
            >
              {{ item.text }}
            </li>
          </ul>

          <div class="input-error" v-if="form.$getError('accountType')">
            {{ form.$getError('accountType')[0] }}
          </div>
        </b-form-group>

        <b-form-group class="col-12">
          <b-img
            class="append-icon"
            :src="require('@/assets/icons/chevron-secondary.svg')"
            @click="showDropdown.siteList = !showDropdown.siteList"
          ></b-img>
          <b-form-input
            :value="selectedSitesName"
            class="input-effect"
            :class="selectedSitesName ? 'has-content' : ''"
            :readonly="true"
            type="text"
            required
            @focus="showDropdown.siteList = true"
            @blur="showDropdown.siteList = false"
          ></b-form-input>
          <label>Sites</label>

          <ul
            v-show="showDropdown.siteList"
            @mousedown.prevent
            class="sites--container"
            @scroll="handleScroll"
          >
            <li
              v-for="(site, index) in siteList"
              :key="index"
              @click="handleSiteSelection(site)"
            >
              {{ site.name }}
            </li>
          </ul>

          <div class="input-error" v-if="form.$getError('sites')">
            {{ form.$getError('sites')[0] }}
          </div>
        </b-form-group>

        <b-button
          class="mt-5"
          variant="outline--primary submit"
          @click="submitInvitation"
          block
        >
          <b-spinner class="mr-2" small v-show="form.$busy"></b-spinner>
          Submit
        </b-button>
        <b-button variant="outline--primary cancel" @click="cancel" block>
          <b-spinner class="mr-2" small v-show="form.$busy"></b-spinner>
          Cancel
        </b-button>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import SearchAll from '@/components/buttons/SearchAll'
import SiteList from '@/components/lists/SiteList'
import BoardList from '@/components/lists/BoardList'
import { QrcodeStream } from 'vue-qrcode-reader'
import Form from '@/utils/form'

function getFirstQuery(name, url) {
  if (!name || !url) {
    return ''
  }
  const results = new RegExp('[\\?&]' + name + '=([^&#]*)').exec(url)
  if (!results) {
    return ''
  }
  return results[1] || ''
}

export default {
  components: {
    SearchAll,
    SiteList,
    BoardList,
    QrcodeStream
  },

  data() {
    return {
      show: false,
      inviteUserModal: false,
      errorMessage: '',
      showHeader: true,
      showDropdown: {
        siteList: false,
        accountType: false
      },
      form: new Form({
        email: '',
        emails: [],
        sites: [],
        accountType: {}
      })
    }
  },

  computed: {
    ...mapState({
      siteListMeta: state => state.site.siteSelectionMeta,
      siteList: state => state.site.siteSelectionList,
      loading: state => state.site.loading,
      user: state => state.auth.user,
      board: state => state.board.boardDetails,
      boardList: state => state.board.list
    }),
    emailErrors() {
      return Object.keys(this.form.$errors).reduce((prev, curr) => {
        if (curr.includes('email')) {
          prev.push(this.form.$getError(curr)[0])
        }

        return prev
      }, [])
    },
    siteErrors() {
      return Object.keys(this.form.$errors).reduce((prev, curr) => {
        if (curr.includes('sites')) {
          prev.push(this.form.$getError(curr)[0])
        }

        return prev
      }, [])
    },
    selectedSitesName() {
      return this.form.sites.map(site => site.name).join(', ')
    },
    selectedSiteIds() {
      return this.form.sites.map(site => site.site_id)
    },
    isOperation() {
      const permittedRoles = [
        'ADMIN',
        'SITE_OWNER',
        'SITE_OPERATION',
        'SITE_HEAD_SPARKY'
      ]
      return (
        this.user && this.user.roles.some(role => permittedRoles.includes(role))
      )
    },
    accountTypes() {
      const roles = [
        {
          text: 'Contractor',
          value: 'sparky'
        }
      ]

      if (this.user) {
        const { roles, is_admin: admin } = this.user
        if (roles.includes('SITE_OWNER') || admin) {
          roles.push({
            text: 'Site Operation',
            value: 'operation'
          })
        }

        if (
          roles.some(role => ['SITE_OWNER', 'SITE_OPERATION'].includes(role)) ||
          admin
        ) {
          roles.push({
            text: 'Head Contractor',
            value: 'head-sparky'
          })
        }

        if (admin) {
          roles.push({
            text: 'Site Owner',
            value: 'owner'
          })
        }
      }

      return roles
    }
  },
  methods: {
    ...mapActions({
      getSites: 'site/getSites',
      scanBoard: 'board/scanBoard',
      sendInvitation: 'user/sendInvitation'
    }),
    ...mapMutations({
      clearSiteList: 'site/clearSiteList',
      clearSiteListSelection: 'site/clearSiteListSelection'
    }),
    handleScroll({ target }) {
      const { current_page: current, last_page: last } = this.siteListMeta
      const e = target
      if (
        Math.ceil(e.scrollTop) + e.clientHeight >= e.scrollHeight &&
        last > current &&
        !this.loading
      ) {
        const page = current + 1
        this.getSites({ page, limit: 10, selection: true })
      }
    },
    cancel() {
      this.form = new Form({
        email: '',
        emails: [],
        sites: [],
        accountType: ''
      })
      this.inviteUserModal = false
    },
    submitInvitation() {
      this.form.$busy = true
      this.form.$clearErrors()

      if (this.form.email) {
        this.handleEmail()
      }

      this.sendInvitation({
        emails: this.form.emails,
        sites: this.form.sites.map(site => ({ site_id: site.site_id })),
        accountType: this.form.accountType
      })
        .then(() => {
          this.form.$busy = false
          this.$bvToast.toast('Invitation has been sent.', {
            title: 'Success',
            variant: 'success',
            solid: true
          })
          this.form = new Form({
            email: '',
            emails: [],
            sites: [],
            accountType: ''
          })
          this.inviteUserModal = false
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },
    handleSiteSelection(site) {
      if (this.selectedSiteIds.includes(site.site_id)) {
        const index = this.selectedSiteIds.findIndex(id => id === site.site_id)
        this.form.sites.splice(index, 1)
      } else {
        this.form.sites.push(site)
      }
    },
    handleEmail() {
      const { email } = this.form
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      const emailValue = email.trim().toLowerCase()

      this.form.$clearErrors()
      if (re.test(emailValue)) {
        if (!this.form.emails.includes(emailValue)) {
          this.form.emails.push(emailValue)
          this.form.email = ''
        }
      } else if (emailValue) {
        this.form.$setErrors({
          email: ['The email is invalid']
        })
      }
    },

    async onDecode(decodedString) {
      let boardId = decodedString
      if (/(http(s?)):\/\//i.test(boardId)) {
        boardId = getFirstQuery('board_id', boardId)
      }

      await this.scanBoard({
        board_id: boardId
      })
        .then(() => {
          this.$router.push({
            name: 'board.board-details-page',
            params: {
              id: this.board.board_id,
              name: this.board.site.name
            }
          })
        })
        .catch(err => {
          if (err.response) {
            this.errorMessage = err.response.data.message
          }
        })
    }
  },
  beforeDestroy() {
    this.clearSiteList()
    this.clearSiteListSelection()
  }
}
</script>

<style lang="scss" scoped>
.sites--container {
  max-height: 200px;
  overflow: auto;
}
.header-image {
  height: 180px;
}
.btn {
  height: 46px;
  background: #ffffff33 0% 0% no-repeat padding-box;
  border: none;
}
.load-btn {
  background: #f2f3f6;
  padding: 0 10px;
  border-radius: 4px;
  height: 40px;
}
.empty-container {
  background: #f5f5f5 0% 0% no-repeat padding-box;
  border-radius: 10px;
  height: 240px;
  text-align: center;
  padding-top: 85px;
  box-shadow: none;
}
.header-actions-container {
  position: absolute;
  color: white;
  top: 90px;
  right: 15px;
  z-index: 2;
  text-shadow: 0px 4px 8px #00000066;
}
.header-text-container {
  position: absolute;
  color: white;
  top: 85px;
  z-index: 2;
  text-shadow: 0px 4px 8px #00000066;
}
.header-text {
  font-size: 24px;
  font-weight: bold;
}
.subheader-text {
  font-size: 12px;
}
.cover-image {
  width: 100%;
  min-height: 160px;
  background: url('~@/assets/images/cover.jpeg');
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  opacity: 0.9;
}
.cover-overlay {
  width: 100%;
  min-height: 160px;
  background-blend-mode: multiply;
  background: transparent url('~@/assets/images/overlay.svg') 0% 0% no-repeat
    padding-box;
  background-size: cover;
  background-repeat: no-repeat;
}

.append-icon {
  top: 16px;
  right: 14px;
}
.form-control:disabled,
.form-control[readonly] {
  background-color: #f8f9fa;
}
.append-icon {
  z-index: 1;
}
.buttontext {
  font-size: 14px;
  font-weight: bold;
}
.buttontext-2 {
  color: grey;
  font-size: 14px;
}
ul {
  position: absolute;
  z-index: 2;
  list-style-type: none;
  margin: 0;
  padding: 0;
  background: #f8f9fa;
  width: 100%;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  margin-top: 2px;
  cursor: pointer;

  li {
    padding: 8px 16px;

    &:hover {
      background: #296dc529;
    }
  }
}

.submit {
  background-color: #106fdc;
  color: #fff;
}

.cancel {
  color: #106fdc;
}

.email-list {
  span {
    margin-right: 5px;
    margin-bottom: 5px;
  }
}
</style>
